<template>

  <v-container fluid class="page-content receiver-location-list-table">

    <!----------------------------->
    <!-- Page Label and Controls -->
    <!----------------------------->
    <v-container fluid class="pa-0">
      <v-row no-gutters>
        <v-col cols="12" class="d-flex align-center">
          <v-icon class="pr-1">mdi-store</v-icon>
          <span class="ml-3 page-label">Receiver Locations</span>
        </v-col>
      </v-row>
      <!-- Find by name -->
      <v-row no-gutters align="center" justify="start" class="mt-4">
        <v-col cols="12" xs="12" sm="8" md="5" lg="4">
          <v-text-field
              clearable
              single-line
              hide-details
              class="ml-2"
              label="Location name"
              append-icon="mdi-magnify"
              v-model="search" />
        </v-col>
      </v-row>
    </v-container>

    <v-data-table
        class="mt-8"
        :headers="tableHeaders"
        :items="receiverLocItems"
        :search="search"
        :options.sync="dataOptions"
        :footer-props="paginationOptions"
        :loading="isDataLoading"
        :server-items-length="totalItems"
        @click:row="goToItemPage">

      <!-- receiver name -->
      <template v-slot:[`item.name`]="{ item }">
        <span style="font-size:1.14em">
          <router-link :to="generateReceiverPageLink(item)">
            {{ item.name }}
          </router-link>
        </span>
      </template>

      <!-- requires appt -->
      <template v-slot:item.requires_appointment="{ item }">
        <span>{{ item.requires_appointment ? 'Yes' : 'No' }}</span>
      </template>

      <!-- last approved date -->
      <template v-slot:item.approved_licenses="{ item }">
        <span>{{ lastApprovedDate(item) }}</span>
      </template>

      <!-- license expires date -->
      <!-- <template v-slot:item.model="{ item }">
        <span>{{ licenseExpiresDate(item) }}</span>
      </template> -->

      <!-- establishment license expires date -->
      <template v-slot:item.establishment_license_expiration="{ item }">
        <div class="d-flex align-center">
          <span>{{ formatDate(item.establishment_license_expiration) }}</span>

          <!-- (alert on expiration) -->
          <v-tooltip top v-if="isLicenseExpired(item.establishment_license_expiration)">
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                  color="yellow darken-3"
                  class="ml-3"
                  style="cursor:pointer;font-size:21px;margin-right:3px;"
                  v-bind="attrs"
                  v-on="on">
                mdi-alert
              </v-icon>
            </template>
            <div>Establishment license has expired.</div>
          </v-tooltip>
        </div>
      </template>

      <!-- drc license expires date -->
      <template v-slot:item.drc_expiration="{ item }">
        <div class="d-flex align-center">
          <span>{{ formatDate(item.drc_expiration) }}</span>

          <!-- (alert on expiration) -->
          <v-tooltip top v-if="isLicenseExpired(item.drc_expiration)">
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                  color="yellow darken-3"
                  class="ml-3"
                  style="cursor:pointer;font-size:21px;margin-right:3px;"
                  v-bind="attrs"
                  v-on="on">
                mdi-alert
              </v-icon>
            </template>
            <div>DRC has expired.</div>
          </v-tooltip>
        </div>
      </template>

      <!-- status -->
      <template v-slot:[`item.receiver_status`]="{ item }">
        <v-chip label outlined :class="item.receiver_status" v-if="item.receiver_status">
          {{ item.receiver_status }}
        </v-chip>
      </template>

      <!-- remove action -->
      <template v-slot:[`item.id`]="{ item }">
        <dialog-confirm-button
            v-if="showRemove"
            :message="generateRemoveConfirmationMessage(item)"
            buttonStyle="plain"
            icon="mdi-trash-can"
            iconColor="grey darken-1"
            v-on:confirmed="emitRemove(item)" />
        </template>
    </v-data-table>

  </v-container>

</template>

<script>
/* global moment */
import DialogConfirmButton from '@/components/app/DialogConfirmButton'

export default {
  name: 'ReceiverLocationListDataTable',

  components: {
    DialogConfirmButton
  },

  props: {
    // An org to filter results (and show context for other operations)
    org: {
      type: Object,
      default: null
    },
    // Enable "remove" action
    showRemove: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      isDataLoading: false,
      receiverLocData: null,
      dataLoadingError: null,
      paginationOptions: {
        'items-per-page-options': [10, 25, 50, 100]
      },
      dataOptions: {
        itemsPerPage: 10,
        // NOTE - Currently enabled for single field sort only !!!
        sortBy: ['name'],
        sortDesc: [false]
      },
      search: null
    }
  },

  computed: {
    tableHeaders () {
      return (this.showRemove)
        ? [
          { text: 'Name', value: 'name' },
          { text: 'Requires Appt?', align: 'center', value: 'requires_appointment' },
          { text: 'Last Approved', sortable: false, value: 'approved_licenses' },
          // { text: 'License Expires', sortable: false, value: 'model' },
          { text: 'Est. License Expires', value: 'establishment_license_expiration' },
          { text: 'DRC Expires', value: 'drc_expiration' },
          { text: 'Status', value: 'receiver_status' },
          { text: '', value: 'id', sortable: false, width: '4%' }
        ]
        : [
          { text: 'Name', value: 'name' },
          { text: 'Requires Appt?', align: 'center', value: 'requires_appointment' },
          { text: 'Last Approved', sortable: false, value: 'approved_licenses' },
          // { text: 'License Expires', sortable: false, value: 'model' },
          { text: 'Est. License Expires', value: 'establishment_license_expiration' },
          { text: 'DRC Expires', value: 'drc_expiration' },
          { text: 'Status', value: 'receiver_status' }
        ]
    },
    userSettings () {
      return this.$store.getters['app/settings']
    },
    receiverLocItems () {
      return (this.receiverLocData) ? this.receiverLocData.items : []
    },
    dataMeta () {
      return (this.receiverLocData) ? this.receiverLocData.meta : {}
    },
    totalItems () {
      return this.dataMeta.total_items || 0
    }
  },

  watch: {
    // Observe changes invoked by the data-table component (e.g. pagination, sorting)
    dataOptions: {
      handler () {
        this.queryData()
      },
      deep: true
    },
    // Observe changes to the search field
    search: function (updated) {
      this.queryData()
    }
  },

  methods: {
    // Refresh action to call from parent components
    refresh () {
      this.queryData()
    },

    // Display a date
    formatDate (date) {
      return (date) ? this.$root.utils.dateFormat(date, 'MMMM D, YYYY') : ''
    },

    // Determine if provided date has expired
    isLicenseExpired (date) {
      return (date)
        ? moment(date).tz(this.userSettings.timezone).isSameOrBefore(moment())
        : false
    },

    // Returns a formatted date for the last approved date
    lastApprovedDate (receiver) {
      if (receiver.approved_licenses && receiver.approved_licenses.length > 0) {
        const sorted = this.$root.utils.orderBy(receiver.approved_licenses, ['approved_at'], ['desc'])
        return this.formatDate(sorted[0].approved_at)
      } else {
        return ''
      }
    },

    // Returns a formatted date for license expiration (for a provided receiver item)
    licenseExpiresDate (receiver) {
      if (receiver.approved_licenses && receiver.approved_licenses.length > 0) {
        const sorted = this.$root.utils.orderBy(receiver.approved_licenses, ['approved_at'], ['desc'])
        const licenseType = sorted[0].license_for_shipping_address
        return this.formatDate(sorted[0][`${licenseType}_expiration`])
      } else {
        return ''
      }
    },

    // Generate link data for receiver location page (for a provided receiver item)
    generateReceiverPageLink (item) {
      if (item && item.model) {
        return {
          name: 'receiver-item',
          params: { receiverId: item.id }
        }
      } else {
        return ''
      }
    },

    // Navigate to receiver location page (for a provided receiver item)
    goToItemPage (item) {
      if (item && item.model) {
        this.$root.goTo('receiver-item', { receiverId: item.id })
      } else {
        console.error('Invalid item data. Cannot invoke item page.')
      }
    },

    // Generate confirmation message on delete action
    generateRemoveConfirmationMessage (item = {}) {
      return (this.org)
        ? `Remove "${item.name}" from ${this.org.name}?`
        : `Are you sure you want to remove "${item.name}"?`
    },

    // Emit remove action
    emitRemove (item) {
      this.$emit('remove', item)
    },

    // Perform query of invites
    async queryData () {
      this.isDataLoading = true

      try {
        // Build sort
        // NOTE - Currently enabled for single field sort only !!!
        let orderBy = null
        const sortBy = this.dataOptions.sortBy
        const sortDesc = this.dataOptions.sortDesc
        if (sortBy && sortBy.length > 0) {
          const isDesc = (sortDesc && sortDesc.length > 0) ? sortDesc[0] : false
          const prefix = (isDesc) ? '-' : ''
          orderBy = `${prefix}${sortBy[0]}`
        }

        // Build pagination
        const page = this.dataOptions.page || 1
        const limit = this.dataOptions.itemsPerPage
        const skip = (page === 1) ? 0 : (page - 1) * limit

        // Build request input
        const params = {
          skip,
          limit,
          with: 'approved_licenses'
        }

        // Apply sorting
        if (orderBy) params.order_by = orderBy

        // Apply search query (for name only)
        if (this.search) params['name.contains'] = this.search

        // Apply org ID filter
        if (this.org) params.org_id = this.org.id

        // Retrieve results
        this.receiverLocData = await this.$store.dispatch('sh/dataApi', {
          method: 'get',
          uri: 'receivers',
          opts: { params }
        })
      } catch (err) {
        this.dataLoadingError = err.message || err.text || err
      } finally {
        this.isDataLoading = false
      }
    }
  }
}
</script>

<style>
  .receiver-location-list-table .theme--dark.v-data-table {
    /* cursor: pointer; */
  }

  .receiver-location-list-table .status-tag {
    text-transform: uppercase !important;
  }

  .receiver-location-list-table .v-data-table > .v-data-table__wrapper > table > tbody > tr > td,
  .receiver-location-list-table .v-data-table > .v-data-table__wrapper > table > thead > tr > td,
  .receiver-location-list-table .v-data-table > .v-data-table__wrapper > table > tfoot > tr > td {
    height: 65px;
  }

  .receiver-location-list-table .v-data-table a {
    text-decoration: none;
  }
  .receiver-location-list-table .v-data-table a:hover {
    text-decoration: underline;
  }
</style>
